import React, { useState, useEffect } from 'react';
import { 
  X, Award, Loader2, MessageSquare, ShoppingBag, MapPin, 
  Factory, Ruler, Clock, CheckCircle, ArrowLeft, Share2, 
  Flag, Users, Heart, Instagram, Twitter, Youtube, Music2, 
  Globe, Link as LinkIcon, ChevronRight, Package, Menu, 
  LayoutGrid, Layers, Bell, Coins, Mail, Info, Store, 
  ShieldCheck, Send, UserPlus, Flame, Ticket, Grid, 
  LayoutTemplate, ArrowRight, Lock, Play, Image as ImageIcon,
  FileText, Building, Briefcase, Calendar, Star, ThumbsUp,
  Phone, Mail as MailIcon, ExternalLink, Edit3
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../services/firebase';
import { doc, setDoc, serverTimestamp, collection, addDoc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { EnquiryModal } from '../shopping/EnquiryModal';

// ============================================================================
// FULL PAGE DESIGNER PROFILE
// ============================================================================
export const DesignerProfilePage = ({ 
  brand: initialBrand, 
  products: initialProducts = [], 
  onClose,
  onSelectProduct,
  onSendMessage
}: any) => {
  const navigate = useNavigate();
  const { designerId: paramDesignerId } = useParams<{ designerId: string }>();
  const { user: currentUser, profile: userProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'shop' | 'company'>('shop');
  const [brand, setBrand] = useState<any>(initialBrand || null);
  const [products, setProducts] = useState<any[]>(initialProducts || []);
  const [loading, setLoading] = useState(!initialBrand);
  const [quoteDetails, setQuoteDetails] = useState({
    productName: '',
    quantity: 1,
    notes: '',
    email: '',
  });

  // 🟢 ENQUIRY MODAL STATE
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [selectedManufacturer, setSelectedManufacturer] = useState<any>(null);

  // 🟢 PRODUCT CLICK HANDLER
  const handleProductClick = (product: any) => {
    console.log('📦 Product clicked in designer profile:', product);
    if (onSelectProduct) {
      onSelectProduct(product);
    } else {
      console.warn('onSelectProduct not provided - product click will not work');
    }
  };

  // 🟢 MESSAGE HANDLER - Fixed
  const handleSendMessage = () => {
    console.log('🔍 Message button clicked in designer profile');
    
    if (!currentUser) {
      window.dispatchEvent(new CustomEvent('open-login-modal'));
      return;
    }
    
    const sellerId = brand?.id || brand?.uid || brand?.workspaceId;
    const brandName = brand?.businessName || brand?.displayName || brand?.name || 'Designer';
    
    if (!sellerId) {
      alert("Unable to message this designer.");
      return;
    }
    
    setIsSubmitting(true);
    
    if (onSendMessage) {
      onSendMessage(sellerId, brandName);
      setIsSubmitting(false);
      if (onClose) onClose();
      return;
    }
    
    try {
      window.dispatchEvent(new CustomEvent('open-messages', {
        detail: { userId: sellerId }
      }));
      if (onClose) onClose();
    } catch (e) {
      console.error(e);
      alert("Failed to open chat.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 🟢 ENQUIRE HANDLER - New
  const handleEnquire = () => {
    console.log('📩 Enquire button clicked in designer profile');
    
    if (!currentUser) {
      window.dispatchEvent(new CustomEvent('open-login-modal'));
      return;
    }

    const sellerId = brand?.id || brand?.uid || brand?.workspaceId;
    const brandName = brand?.businessName || brand?.displayName || brand?.name || 'Designer';
    const brandLocation = brand?.location || brand?.sellerLocation || 'Global';

    setSelectedManufacturer({
      id: sellerId,
      businessName: brandName,
      displayName: brandName,
      photoURL: brand?.photoURL || brand?.logoUrl || brand?.avatar,
      logoUrl: brand?.logoUrl || brand?.photoURL || brand?.avatar,
      location: brandLocation,
      moq: brand?.moq || brand?.defaultMOQ || 50,
      leadTime: brand?.leadTime || brand?.defaultLeadTime || '4-6 Weeks',
    });
    setShowEnquiryModal(true);
  };

  // Fetch designer data if not provided as props
  useEffect(() => {
    const fetchDesignerData = async () => {
      const designerId = initialBrand?.id || initialBrand?.uid || paramDesignerId;
      
      if (!designerId) {
        console.log('No designer ID found');
        return;
      }

      try {
        setLoading(true);
        
        // Fetch designer profile
        let brandData = initialBrand;
        if (!brandData) {
          const userDoc = await getDoc(doc(db, 'users', designerId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            brandData = {
              id: userDoc.id,
              uid: userDoc.id,
              ...userData,
              businessName: userData.businessName || userData.displayName || userData.username || 'Designer',
              displayName: userData.displayName || userData.businessName || userData.username || 'Designer',
              name: userData.businessName || userData.displayName || userData.username || 'Designer',
              location: userData.location || userData.sellerLocation || 'Global',
              photoURL: userData.photoURL || userData.logoUrl || null,
              logoUrl: userData.logoUrl || userData.photoURL || null,
              bio: userData.bio || userData.description || '',
              description: userData.description || userData.bio || '',
              theme: userData.theme || userData.storefront?.theme || 'minimal',
              materials: userData.materials || userData.storefront?.materials || [],
              storefront: userData.storefront || {},
              verified: userData.verified || false,
              followersCount: userData.followersCount || userData.followers || 0,
              instagramHandle: userData.instagramHandle || '',
              twitterHandle: userData.twitterHandle || '',
              linkedinHandle: userData.linkedinHandle || '',
              youtubeHandle: userData.youtubeHandle || '',
              footerText: userData.footerText || '',
              maxCapacity: userData.maxCapacity || 'Contact for info',
              leadTime: userData.leadTime || '4-6 Weeks',
              certifications: userData.certifications || 'Verified Merchant',
              techPackTemplate: userData.techPackTemplate || '',
              // Company profile fields
              companyName: userData.companyName || userData.businessName || 'Design Studio',
              yearEstablished: userData.yearEstablished || '',
              employeeCount: userData.employeeCount || '',
              companyType: userData.companyType || 'Design Studio',
              productionCapabilities: userData.productionCapabilities || [],
              minimumOrderValue: userData.minimumOrderValue || 'Contact for info',
              responseTime: userData.responseTime || 'Within 24 hours',
              languages: userData.languages || ['English'],
              paymentTerms: userData.paymentTerms || 'Contact for info',
            };
            setBrand(brandData);
          } else {
            console.log('No user found for ID:', designerId);
            return;
          }
        } else {
          setBrand(brandData);
        }

        // 🟢 FETCH PRODUCTS
        if (brandData) {
          const designerIdToUse = brandData.id || brandData.uid || designerId;
          
          // Try querying by sellerId
          let productsQuery = query(
            collection(db, 'products'),
            where('sellerId', '==', designerIdToUse)
          );
          let productsSnap = await getDocs(productsQuery);
          
          // If no products found, try workspaceId
          if (productsSnap.empty) {
            console.log('No products found by sellerId, trying workspaceId...');
            productsQuery = query(
              collection(db, 'products'),
              where('workspaceId', '==', designerIdToUse)
            );
            productsSnap = await getDocs(productsQuery);
          }
          
          // If still no products, try businessId
          if (productsSnap.empty) {
            console.log('No products found by workspaceId, trying businessId...');
            productsQuery = query(
              collection(db, 'products'),
              where('businessId', '==', designerIdToUse)
            );
            productsSnap = await getDocs(productsQuery);
          }

          if (productsSnap.empty) {
            console.log('No products found by specific fields, trying workspaceType filter...');
            productsQuery = query(
              collection(db, 'products'),
              where('workspaceType', '==', 'designer')
            );
            productsSnap = await getDocs(productsQuery);
            
            const allProducts = productsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const filteredProducts = allProducts.filter((p: any) => 
              p.sellerId === designerIdToUse || 
              p.workspaceId === designerIdToUse || 
              p.businessId === designerIdToUse ||
              p.sellerId === brandData.id ||
              p.workspaceId === brandData.id
            );
            setProducts(filteredProducts);
            console.log('DEBUG: Found products by manual filter:', filteredProducts.length);
            return;
          }

          const productList = productsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setProducts(productList);
          console.log('DEBUG: Found products:', productList.length);
        }
      } catch (error) {
        console.error('Error fetching designer data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDesignerData();
  }, [initialBrand, paramDesignerId]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen w-full flex items-center justify-center ${brand?.theme === 'industrial' || brand?.theme === 'avant' ? 'bg-black' : 'bg-white'}`}>
        <Loader2 className={`animate-spin ${brand?.theme === 'industrial' || brand?.theme === 'avant' ? 'text-white' : 'text-black'}`} size={40} />
      </div>
    );
  }

  if (!brand) return null;

  // Support DesignerStorefront data structure
  const brandId = brand.uid || brand.id || brand.workspaceId || brand.businessId;
  
  const brandName = brand.businessName || 
                   brand.displayName || 
                   brand.rootName || 
                   brand.name || 
                   'Designer';
  
  const brandImage = brand.photoURL || 
                    brand.logoUrl || 
                    brand.logo || 
                    brand.avatar || 
                    'https://placehold.co/150x150/eee/999?text=Studio';
  
  const location = brand.location || 
                  brand.sellerLocation || 
                  brand.userLocation || 
                  brand.address?.city || 
                  'Global';
  
  const bio = brand.bio || 
             brand.description || 
             brand.about || 
             '';
  
  const maxCapacity = brand.maxCapacity || 
                     brand.capacity || 
                     'Contact for info';
  
  const leadTime = brand.leadTime || 
                  brand.defaultLeadTime || 
                  '4-6 Weeks';
  
  const certifications = brand.certifications || 
                        brand.verified ? 'Verified Merchant' : 'Standard';

  const theme = brand.theme || brand.storefront?.theme || 'minimal';
  const isDark = theme === 'industrial' || theme === 'avant' || theme === 'studio';

  const materials = brand.materials || brand.storefront?.materials || [];
  const followersCount = brand.followersCount || brand.followers || 0;

  const showMOQ = brand.storefront?.showMOQ !== undefined ? brand.storefront.showMOQ : true;
  const showLeadTimes = brand.storefront?.showLeadTimes !== undefined ? brand.storefront.showLeadTimes : true;
  const showTechPacks = brand.storefront?.showTechPacks !== undefined ? brand.storefront.showTechPacks : true;
  const layout = brand.storefront?.layout || 'grid';

  // Company profile fields
  const companyName = brand.companyName || brand.businessName || 'Design Studio';
  const yearEstablished = brand.yearEstablished || '';
  const employeeCount = brand.employeeCount || '';
  const companyType = brand.companyType || 'Design Studio';
  const productionCapabilities = brand.productionCapabilities || [];
  const minimumOrderValue = brand.minimumOrderValue || 'Contact for info';
  const responseTime = brand.responseTime || 'Within 24 hours';
  const languages = brand.languages || ['English'];
  const paymentTerms = brand.paymentTerms || 'Contact for info';

  const socialLinks = {
    instagram: brand.instagramHandle || brand.storefront?.instagramHandle || '',
    linkedin: brand.linkedinHandle || brand.storefront?.linkedinHandle || '',
    twitter: brand.twitterHandle || brand.storefront?.twitterHandle || '',
    youtube: brand.youtubeHandle || brand.storefront?.youtubeHandle || '',
  };

  const handleContact = async () => {
    if (!currentUser) return navigate('/login');
    setIsSubmitting(true);
    try {
      const chatId = [currentUser.uid, brandId].sort().join('_');
      await setDoc(doc(db, "messages", chatId), {
        participants: [currentUser.uid, brandId],
        participantNames: { 
          [currentUser.uid]: userProfile?.displayName || 'Buyer', 
          [brandId]: brandName 
        },
        lastMessage: `Hello, I'm interested in working with your studio: ${brandName}`,
        updatedAt: serverTimestamp()
      }, { merge: true });
      navigate(`/messages/${chatId}`);
    } catch (e) {
      console.error(e);
      alert("Failed to open chat.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestQuote = () => {
    if (!currentUser) return navigate('/login');
    setShowQuoteModal(true);
  };

  const handleSubmitQuote = async () => {
    if (!currentUser) return;
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "quotes"), {
        designerId: brandId,
        designerName: brandName,
        buyerId: currentUser.uid,
        buyerName: userProfile?.displayName || 'Anonymous',
        buyerEmail: userProfile?.email || quoteDetails.email,
        productName: quoteDetails.productName || 'General Inquiry',
        quantity: Number(quoteDetails.quantity) || 1,
        notes: quoteDetails.notes || '',
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      alert("Quote request sent successfully!");
      setShowQuoteModal(false);
      setQuoteDetails({ productName: '', quantity: 1, notes: '', email: '' });
    } catch (e) {
      console.error(e);
      alert("Failed to send quote request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/designer/${brandId}`;
    navigator.clipboard.writeText(shareUrl);
    setShowShareMenu(false);
    alert("Link copied to clipboard!");
  };

  const handleNativeShare = async () => {
    const shareUrl = `${window.location.origin}/designer/${brandId}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: brandName,
          text: `Check out ${brandName} on Scoop Hype! 🔥`,
          url: shareUrl
        });
        setShowShareMenu(false);
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      handleCopyLink();
    }
  };

  return (
    <div className={`min-h-screen w-full flex flex-col ${isDark ? 'bg-black text-white' : 'bg-white text-black'}`}>
      
      {/* Navigation - Clean header */}
      <div className={`sticky top-0 z-10 px-4 sm:px-6 md:px-8 py-3 sm:py-4 flex justify-between items-center border-b ${isDark ? 'border-white/10 bg-black/95' : 'border-gray-200 bg-white/95'} backdrop-blur-sm`}>
        <button 
          onClick={handleClose} 
          className={`flex items-center gap-2 transition-colors text-sm font-bold ${isDark ? 'hover:text-white text-zinc-400' : 'hover:text-black text-gray-500'}`}
        >
          <ArrowLeft size={18} />
          <span className="hidden sm:inline uppercase tracking-widest">Back</span>
        </button>
        
        <h2 className={`text-xs sm:text-sm font-black uppercase tracking-widest truncate max-w-[150px] sm:max-w-xs ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
          {brandName}
        </h2>
        
        <button 
          onClick={() => setShowShareMenu(true)}
          className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
        >
          <Share2 size={18} />
        </button>
      </div>

      {/* Profile Content */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Logo + Name Section - Centered */}
          <div className="flex flex-col items-center text-center">
            {/* Logo */}
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-white/10 bg-zinc-800 overflow-hidden shadow-2xl flex items-center justify-center mb-2 sm:mb-3">
              {brandImage ? (
                <img src={brandImage} className="w-full h-full object-cover" alt={brandName} />
              ) : (
                <span className="text-2xl sm:text-3xl font-black text-zinc-500 uppercase">{brandName.charAt(0)}</span>
              )}
            </div>
            
            <h1 className="text-xl sm:text-2xl md:text-3xl font-black uppercase tracking-tight">
              {brandName}
            </h1>
            
            <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
              <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
                <MapPin size={12} /> {location}
              </div>
              {brand.verified && (
                <CheckCircle size={14} className="text-orange-500" fill="currentColor" />
              )}
              {brand.verified && (
                <span className="text-[8px] font-black uppercase bg-orange-500/10 text-orange-500 px-2 py-0.5 rounded-full border border-orange-500/20">
                  Verified
                </span>
              )}
            </div>

            {/* Bio */}
            {bio && (
              <p className={`text-sm mt-2 max-w-xl mx-auto ${isDark ? 'text-zinc-300' : 'text-gray-600'}`}>
                {bio}
              </p>
            )}

            {/* Social Icons */}
            {(socialLinks.instagram || socialLinks.twitter || socialLinks.linkedin || socialLinks.youtube) && (
              <div className="flex flex-wrap justify-center gap-2 mt-3">
                {socialLinks.instagram && (
                  <a href={`https://instagram.com/${socialLinks.instagram}`} target="_blank" rel="noreferrer" className={`p-1.5 border rounded-lg transition-colors ${isDark ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                    <Instagram size={14} />
                  </a>
                )}
                {socialLinks.twitter && (
                  <a href={`https://twitter.com/${socialLinks.twitter}`} target="_blank" rel="noreferrer" className={`p-1.5 border rounded-lg transition-colors ${isDark ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                    <Twitter size={14} />
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a href={`https://linkedin.com/in/${socialLinks.linkedin}`} target="_blank" rel="noreferrer" className={`p-1.5 border rounded-lg transition-colors ${isDark ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                    <Globe size={14} />
                  </a>
                )}
                {socialLinks.youtube && (
                  <a href={socialLinks.youtube} target="_blank" rel="noreferrer" className={`p-1.5 border rounded-lg transition-colors ${isDark ? 'border-white/10 hover:bg-white/10 text-white/60 hover:text-white' : 'border-gray-200 hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`}>
                    <Youtube size={14} />
                  </a>
                )}
              </div>
            )}

            {/* 🟢 ACTION BUTTONS - Fixed with Enquire */}
            <div className="flex gap-3 w-full max-w-md mt-4">
              <button 
                onClick={handleSendMessage}
                className={`flex-1 py-2.5 sm:py-3 font-black uppercase text-[10px] sm:text-xs tracking-widest rounded-xl transition-all flex justify-center items-center gap-2 ${
                  isDark 
                    ? 'border border-white/20 text-white hover:bg-white/10' 
                    : 'border border-gray-300 text-gray-900 hover:bg-gray-100'
                }`}
              >
                <MessageSquare size={14} /> 
                <span>Message</span>
              </button>
              <button 
                onClick={handleEnquire}
                className="flex-1 py-2.5 sm:py-3 bg-orange-500 text-white font-black uppercase text-[10px] sm:text-xs tracking-widest rounded-xl hover:bg-orange-600 transition-all flex justify-center items-center gap-2 shadow-lg shadow-orange-500/30"
              >
                <Send size={14} /> 
                <span>Enquire</span>
              </button>
            </div>
          </div>

          {/* 🟢 STATS */}
          <div className="grid grid-cols-3 gap-2 mt-4 max-w-md mx-auto">
            <div className={`p-2 rounded-lg border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
              <span className={`text-[7px] font-black uppercase block text-center ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
                <Factory size={10} className="inline mr-0.5" /> Capacity
              </span>
              <p className={`font-black text-[10px] text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {maxCapacity}
              </p>
            </div>
            <div className={`p-2 rounded-lg border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
              <span className={`text-[7px] font-black uppercase block text-center ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
                <Clock size={10} className="inline mr-0.5" /> Lead Time
              </span>
              <p className={`font-black text-[10px] text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {leadTime}
              </p>
            </div>
            <div className={`p-2 rounded-lg border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
              <span className={`text-[7px] font-black uppercase block text-center ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
                <Users size={10} className="inline mr-0.5" /> Followers
              </span>
              <p className={`font-black text-[10px] text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {followersCount}
              </p>
            </div>
          </div>

          {/* Materials */}
          {materials.length > 0 && (
            <div className="mt-4">
              <h3 className={`text-[9px] sm:text-[10px] font-black uppercase tracking-widest mb-1.5 text-center ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
                <Ruler size={10} className="inline mr-1" /> Materials
              </h3>
              <div className="flex flex-wrap justify-center gap-1.5">
                {materials.map((material: string) => (
                  <span key={material} className={`px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[8px] sm:text-[10px] font-bold ${isDark ? 'bg-white/10 text-white border border-white/5' : 'bg-gray-100 text-gray-700'}`}>
                    {material}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 🟢 TABS - Changed "About" to "Company Profile" */}
          <div className={`flex justify-center border-b mt-4 sm:mt-5 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            {[
              { id: 'shop', label: 'Products & Services' },
              { id: 'company', label: 'Company Profile' }
            ].map(tab => (
              <button 
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 text-[10px] sm:text-xs font-black uppercase tracking-[0.15em] sm:tracking-[0.2em] transition-colors border-b-2 ${
                  activeTab === tab.id 
                    ? `border-orange-500 ${isDark ? 'text-white' : 'text-gray-900'}`
                    : `border-transparent ${isDark ? 'text-zinc-500 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="mt-3 sm:mt-4">
            {activeTab === 'shop' && (
              <div>
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <h3 className={`text-[9px] sm:text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
                    <Package size={10} className="inline mr-1" /> Services ({products.length})
                  </h3>
                </div>
                
                {products.length === 0 ? (
                  <div className={`text-center py-10 sm:py-12 rounded-2xl border border-dashed ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                    <Package className={`mx-auto mb-2 ${isDark ? 'text-zinc-600' : 'text-gray-300'}`} size={28} />
                    <p className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest ${isDark ? 'text-zinc-400' : 'text-gray-400'}`}>
                      No services listed yet.
                    </p>
                  </div>
                ) : (
                  <div className={`grid ${layout === 'grid' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'} gap-2 sm:gap-3`}>
                    {products.map((p: any) => (
                      <div 
                        key={p.id} 
                        onClick={() => handleProductClick(p)}
                        className={`group cursor-pointer p-2.5 sm:p-3 rounded-xl border transition-all hover:scale-[1.02] active:scale-95 ${
                          isDark 
                            ? 'border-white/10 hover:border-orange-500/50 bg-white/5 hover:bg-white/10' 
                            : 'border-gray-200 hover:border-orange-500 bg-white hover:shadow-lg'
                        }`}
                      >
                        <div className="aspect-square bg-zinc-800 rounded-lg mb-1.5 sm:mb-2 overflow-hidden">
                          {p.image || p.images?.[0] ? (
                            <img src={p.image || p.images?.[0]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" alt={p.name} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package size={20} className={isDark ? 'text-zinc-600' : 'text-gray-300'} />
                            </div>
                          )}
                        </div>
                        <h4 className={`font-black text-[10px] sm:text-xs truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {p.name}
                        </h4>
                        <p className={`text-[7px] sm:text-[9px] font-bold uppercase truncate ${isDark ? 'text-zinc-400' : 'text-gray-400'}`}>
                          {p.category || 'Service'}
                        </p>
                        <div className="flex flex-wrap items-center justify-between gap-1 mt-1">
                          <p className="text-[10px] sm:text-xs font-black text-orange-500">
                            R{p.price}
                          </p>
                          {showMOQ && p.moq && (
                            <span className={`text-[6px] sm:text-[8px] font-bold ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>
                              MOQ: {p.moq}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'company' && (
              <div className="space-y-4">
                {/* Company Profile Header */}
                <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Building size={18} className="text-orange-500" />
                    <h3 className={`text-[10px] sm:text-xs font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Company Overview
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className={`text-[9px] font-bold uppercase tracking-widest ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>Company Name</p>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{companyName}</p>
                    </div>
                    {yearEstablished && (
                      <div>
                        <p className={`text-[9px] font-bold uppercase tracking-widest ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>Established</p>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{yearEstablished}</p>
                      </div>
                    )}
                    {employeeCount && (
                      <div>
                        <p className={`text-[9px] font-bold uppercase tracking-widest ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>Team Size</p>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{employeeCount}</p>
                      </div>
                    )}
                    <div>
                      <p className={`text-[9px] font-bold uppercase tracking-widest ${isDark ? 'text-zinc-500' : 'text-gray-400'}`}>Company Type</p>
                      <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{companyType}</p>
                    </div>
                  </div>
                </div>

                {/* Production Capabilities */}
                {productionCapabilities.length > 0 && (
                  <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <Factory size={18} className="text-orange-500" />
                      <h3 className={`text-[10px] sm:text-xs font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Production Capabilities
                      </h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {productionCapabilities.map((capability: string) => (
                        <span key={capability} className={`px-3 py-1 rounded-full text-[10px] font-bold ${isDark ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' : 'bg-orange-100 text-orange-700'}`}>
                          {capability}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Company Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Materials */}
                  {materials.length > 0 && (
                    <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <Ruler size={16} className="text-orange-500" />
                        <h3 className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Materials
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {materials.map((material: string) => (
                          <span key={material} className={`px-2 py-0.5 rounded-full text-[8px] font-bold ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}>
                            {material}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Lead Time & MOQ */}
                  <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <Clock size={16} className="text-orange-500" />
                      <h3 className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Production Info
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {showLeadTimes && (
                        <div className="flex justify-between">
                          <span className={`text-[9px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Lead Time</span>
                          <span className={`text-[10px] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{leadTime}</span>
                        </div>
                      )}
                      {showMOQ && (
                        <div className="flex justify-between">
                          <span className={`text-[9px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>MOQ</span>
                          <span className={`text-[10px] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{brand.moq || brand.defaultMOQ || 50} units</span>
                        </div>
                      )}
                      {minimumOrderValue && (
                        <div className="flex justify-between">
                          <span className={`text-[9px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Min. Order Value</span>
                          <span className={`text-[10px] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{minimumOrderValue}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Languages & Payment */}
                  <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <Globe size={16} className="text-orange-500" />
                      <h3 className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Business Details
                      </h3>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className={`text-[9px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Languages</span>
                        <span className={`text-[10px] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{languages.join(', ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`text-[9px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Payment Terms</span>
                        <span className={`text-[10px] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{paymentTerms}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={`text-[9px] ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Response Time</span>
                        <span className={`text-[10px] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{responseTime}</span>
                      </div>
                    </div>
                  </div>

                  {/* Tech Packs */}
                  {showTechPacks && (
                    <div className={`p-4 rounded-2xl border ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <FileText size={16} className="text-orange-500" />
                        <h3 className={`text-[10px] font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Tech Packs
                        </h3>
                      </div>
                      <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-600'}`}>
                        {brand.techPackTemplate ? 'Tech pack templates available upon request' : 'Contact for tech pack details'}
                      </p>
                    </div>
                  )}

                  {/* Footer Text */}
                  {brand.footerText && (
                    <div className={`p-4 rounded-2xl border col-span-1 sm:col-span-2 ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                      <p className={`text-xs text-center ${isDark ? 'text-zinc-400' : 'text-gray-400'}`}>
                        {brand.footerText}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quote Request Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 z-[130] bg-black/80 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowQuoteModal(false)}>
          <div className="bg-zinc-900 border border-white/10 rounded-[2rem] p-6 sm:p-8 w-full max-w-md shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-black uppercase italic tracking-widest text-xl">
                Request Quote
              </h3>
              <button onClick={() => setShowQuoteModal(false)} className="text-zinc-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <p className="text-zinc-400 text-sm mb-6">
              Request a production quote from <span className="text-white font-bold">{brandName}</span>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                  Product / Service
                </label>
                <input
                  type="text"
                  value={quoteDetails.productName}
                  onChange={(e) => setQuoteDetails({...quoteDetails, productName: e.target.value})}
                  placeholder="e.g. Custom Hoodies, Screen Printing"
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm outline-none focus:border-orange-500 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  value={quoteDetails.quantity}
                  onChange={(e) => setQuoteDetails({...quoteDetails, quantity: parseInt(e.target.value) || 1})}
                  placeholder="e.g. 100"
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm outline-none focus:border-orange-500 transition-colors"
                  min="1"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                  Email (for quote response)
                </label>
                <input
                  type="email"
                  value={quoteDetails.email || userProfile?.email || ''}
                  onChange={(e) => setQuoteDetails({...quoteDetails, email: e.target.value})}
                  placeholder={userProfile?.email || 'your@email.com'}
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm outline-none focus:border-orange-500 transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 mb-1">
                  Notes / Requirements
                </label>
                <textarea
                  value={quoteDetails.notes}
                  onChange={(e) => setQuoteDetails({...quoteDetails, notes: e.target.value})}
                  placeholder="Describe your requirements, timeline, budget..."
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm outline-none focus:border-orange-500 transition-colors resize-none h-24"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setShowQuoteModal(false)} 
                className="flex-1 py-3 bg-white/5 text-white border border-white/10 rounded-xl font-bold uppercase text-xs tracking-widest hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmitQuote} 
                disabled={isSubmitting}
                className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-bold uppercase text-xs tracking-widest hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : 'Send Quote Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowShareMenu(false)}>
          <div className="bg-zinc-900 border border-white/10 rounded-[2rem] p-6 sm:p-8 w-full max-w-sm text-center shadow-2xl animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/5 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Share2 className="text-white" size={28} />
            </div>
            <h3 className="text-white font-black uppercase italic tracking-widest text-lg sm:text-xl mb-2">Share Profile</h3>
            <p className="text-zinc-500 text-xs mb-6 sm:mb-8 uppercase font-bold tracking-tighter">Share this designer's profile.</p>
            <div className="space-y-3">
              <button onClick={handleNativeShare} className="w-full py-3 sm:py-4 bg-white text-black rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-200 transition-all active:scale-95 flex items-center justify-center gap-2">
                <Users size={16} /> Share
              </button>
              <button onClick={handleCopyLink} className="w-full py-3 sm:py-4 bg-zinc-800 text-white rounded-xl font-black uppercase text-xs tracking-widest hover:bg-zinc-700 transition-all active:scale-95 flex items-center justify-center gap-2">
                <LinkIcon size={16} /> Copy Link
              </button>
            </div>
            <button onClick={() => setShowShareMenu(false)} className="mt-6 text-zinc-600 uppercase font-black text-[10px] hover:text-white transition-colors tracking-[0.2em]">Close</button>
          </div>
        </div>
      )}

      {/* 🟢 ENQUIRY MODAL */}
      <EnquiryModal
        isOpen={showEnquiryModal}
        onClose={() => {
          setShowEnquiryModal(false);
          setSelectedManufacturer(null);
        }}
        manufacturer={selectedManufacturer}
        onEnquirySent={() => {
          setShowEnquiryModal(false);
          setSelectedManufacturer(null);
        }}
      />
    </div>
  );
};

// ============================================================================
// MODAL WRAPPER - FULL SCREEN
// ============================================================================
export const DesignerProfileModal = ({ 
  isOpen, 
  onClose, 
  brand, 
  products = [],
  onSelectProduct,
  onSendMessage
}: any) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[1000] w-full h-full">
      <DesignerProfilePage 
        brand={brand} 
        products={products} 
        onClose={onClose}
        onSelectProduct={onSelectProduct}
        onSendMessage={onSendMessage}
      />
    </div>
  );
};

export default DesignerProfileModal;